import { defineComponent as _defineComponent } from 'vue'
import { reactive, computed, watch, onBeforeUnmount } from 'vue';
import {
  BaseFormModel,
  FieldOptions,
  FormDefinition,
  FormEvent,
  ValidationRuleSet,
} from 'ah-common-lib/src/form/interfaces';
import { getChildModel, makeFormModel, setState } from 'ah-common-lib/src/form/helpers';
import { financialAmountField } from 'ah-common-lib/src/form/models';
import { useTradeState } from '../../';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { FundingType, QuotePriceResponse, TradeFundingDetails } from 'ah-api-gateways';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { ValidatedForm } from 'ah-common-lib/src/form/components';
import { debounce } from 'lodash';
import { required } from '@vuelidate/validators';
import CollateralTransactionExecutor from 'ah-trades/src/components/collaterals/CollateralTransactionExecutor.vue';
import { compareState } from 'ah-common-lib/src/form/validators';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradeCollateralCreditFundForm',
  props: {
    tradePriceResponse: null,
    tradeFundingDetails: null
  },
  emits: ["update:tradeFundingDetails"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:tradeFundingDetails', value: Partial<TradeFundingDetails>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  tradePriceResponse: QuotePriceResponse;
  tradeFundingDetails?: Partial<TradeFundingDetails>;
};





const tradeState = useTradeState();

const onBehalfOf = useOnBehalfOf();

const collaterals = computed(() => {
  const clientId = onBehalfOf.value
    ? onBehalfOf.value.id
    : tradeState.store.useAuthStore().loggedInIdentity?.client?.id;

  if (clientId) {
    return tradeState.store.useWalletsStore().getCollaterals(clientId);
  }
  return null;
});

const areCollateralsLoading = computed(() =>
  tradeState.store.useWalletsStore().areCollateralsLoading(onBehalfOf.value?.id)
);

const sufficientCollateral = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }

  return (
    props.tradePriceResponse.homeCurrencyInitialMarginAmount <= 0 ||
    collaterals.value.marginCollateralAvailable >= props.tradePriceResponse.homeCurrencyInitialMarginAmount
  );
});

const shouldBeAbleToPost = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }
  return (
    !sufficientCollateral.value ||
    collaterals.value.initialMarginCollateralAvailable + collaterals.value.initialMarginCreditAvailable <
      props.tradePriceResponse.homeCurrencyInitialMarginAmount
  );
});

const sufficientInitialCollateral = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }
  return (
    sufficientCollateral.value &&
    collaterals.value.initialMarginCollateralAvailable >= (forms.collateral.form.value ?? 0)
  );
});

const allCollateralUsed = computed(() => {
  if (
    !collaterals.value ||
    areCollateralsLoading.value ||
    creditAtZero.value ||
    forms.collateral.form.value === undefined
  ) {
    return false;
  }
  return collaterals.value.initialMarginCollateralAvailable === forms.collateral.form.value;
});

const creditAtZero = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }

  return collaterals.value?.initialMarginCreditAvailable === 0;
});

const sufficientInitialCredit = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value || creditAtZero.value) {
    return false;
  }
  return sufficientCollateral.value && collaterals.value.initialMarginCreditAvailable >= (forms.credit.form.value ?? 0);
});

const initialMarginCreditAvailable = computed(() => collaterals.value?.initialMarginCreditAvailable ?? 0);

const initialMarginCollateralAvailable = computed(() => collaterals.value?.initialMarginCollateralAvailable ?? 0);

const marginRequirements = computed(() => props.tradePriceResponse.homeCurrencyInitialMarginAmount ?? 0);

const currency = computed(() => props.tradePriceResponse.homeCurrency);

const areFormsValid = computed(() => !forms.collateral.validation?.$invalid && !forms.credit.validation?.$invalid);

const isValid = computed(
  () => areFormsValid.value && sufficientInitialCollateral.value && sufficientInitialCredit.value
);

const formState: FieldOptions = {
  inputClass: 'form-control bordered',
  errorMessages: {
    minValue: 'The amount must be more than GBP 0.00.',
    sameValue: 'The amount cannot be the entire value of initial margin requirements.',
    maxValue: 'The amount cannot exceed the value of initial margin requirements.',
  },
};

const formValidations: ValidationRuleSet = {
  required,
  minValue: compareState('collateral', (val) => typeof val !== 'number' || val > 0),
  sameValue: compareState('collateral', (val) => typeof val !== 'number' || val !== marginRequirements.value),
  maxValue: compareState('collateral', (val) => typeof val !== 'number' || val <= marginRequirements.value),
};

const forms = {
  collateral: reactive<FormDefinition>({
    form: makeFormModel({
      name: 'collateralContributionForm',
      fieldType: 'form',
      fields: [
        financialAmountField('value', '', formState, {
          ...formValidations,
          maxValue: compareState(
            'collateral',
            (val) => typeof val !== 'number' || val <= initialMarginCollateralAvailable.value
          ),
        }),
      ],
    }),
    validation: null,
  }),
  credit: reactive<FormDefinition>({
    form: makeFormModel({
      name: 'creditContributionForm',
      fieldType: 'form',
      fields: [
        financialAmountField('value', '', formState, {
          ...formValidations,
          maxValue: compareState(
            'collateral',
            (val) => typeof val !== 'number' || val <= initialMarginCreditAvailable.value
          ),
        }),
      ],
    }),
    validation: null,
  }),
};

tradeState.store.useWalletsStore().loadCollaterals({ force: false, clientId: onBehalfOf.value?.id });

function emitValues() {
  emit('update:tradeFundingDetails', {
    initialMarginCollateralFundingAmount: forms.collateral.form.value ?? 0,
    initialMarginCreditFundingAmount: forms.credit.form.value ?? 0,
    initialMarginFundingCurrency: currency.value,
    initialMarginFundingType: FundingType.COLLATERAL_AND_CREDIT,
    valid: isValid.value,
  });
}

const debouncedCalc = debounce((changed: FormDefinition, change: FormDefinition) => {
  change.form.value = marginRequirements.value - changed.form.value;
  change.validation?.$touch();
  emitValues();
}, 500);

function setFormPlaceholder(field: BaseFormModel, placeholder = 'Calculating...') {
  setState(field, 'placeholder', placeholder);
}

function onFormEvent(event: FormEvent, formKey: 'credit' | 'collateral' = 'collateral') {
  if (event.event === 'form-field-set-value') {
    const changedForm = forms[formKey];
    const reactingForm = forms[formKey === 'collateral' ? 'credit' : 'collateral'];
    const reactingFormModel = getChildModel(reactingForm.form, 'value')!;
    reactingForm.form.value = '';
    reactingForm.validation?.$reset();

    if (!changedForm.validation?.$invalid) {
      setFormPlaceholder(reactingFormModel);
      debouncedCalc(changedForm, reactingForm);
    } else {
      setFormPlaceholder(reactingFormModel, '');
    }
  }
}

watch(
  () => props.tradeFundingDetails,
  () => {
    if (props.tradeFundingDetails) {
      forms.collateral.form.value = props.tradeFundingDetails.initialMarginCollateralFundingAmount;
      forms.credit.form.value = props.tradeFundingDetails.initialMarginCreditFundingAmount;
    }
  },
  { immediate: true }
);

watch(
  creditAtZero,
  () => {
    setState(getChildModel(forms.collateral.form, 'value')!, 'readonly', creditAtZero.value);
    setState(getChildModel(forms.credit.form, 'value')!, 'readonly', creditAtZero.value);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  debouncedCalc.cancel();
});

expose({ isValid });

return { __sfc: true,props, emit, tradeState, onBehalfOf, collaterals, areCollateralsLoading, sufficientCollateral, shouldBeAbleToPost, sufficientInitialCollateral, allCollateralUsed, creditAtZero, sufficientInitialCredit, initialMarginCreditAvailable, initialMarginCollateralAvailable, marginRequirements, currency, areFormsValid, isValid, formState, formValidations, forms, emitValues, debouncedCalc, setFormPlaceholder, onFormEvent, formatCurrencyValue, ValidatedForm, CollateralTransactionExecutor }
}

})